import React, { Suspense } from 'react'
import { useLocation } from 'react-router'
import { useMount } from 'react-use'

import useAppDispatch from '../../../hooks/useAppDispatch'
import { useAppStateQuery } from '../../../hooks/useAppStateQuery'
import { useGroupDashboardSidebarLinks } from '../../../hooks/useGroupDashboardSidebarLinks'
import { matchesPaths } from '../../../lib/helpers/routeHelpers'
import { ROUTES } from '../../../lib/routes'
import { LAYOUTS, setLayout } from '../../../redux/slices/layout'
import Main from '../../misc/Main'
import PageContent from '../../misc/PageContent'
import SentryErrorBoundary from '../../misc/SentryErrorBoundary'
import LoadingWidget from '../../widgets/LoadingWidget'
import Footer from './components/Footer'
import Navbar from './components/Navbar/Navbar'
import Sidebar from './components/sidebar/Sidebar'

interface Props {
  children: React.ReactNode
  padded?: boolean
}

const GroupDashboardLayout: React.FC<Props> = ({ children, padded = true }) => {
  const location = useLocation()
  const sidebarLinks = useGroupDashboardSidebarLinks()
  const dispatch = useAppDispatch()

  useMount(() => {
    dispatch(setLayout(LAYOUTS.groupDashboard))
  })

  const appStateQuery = useAppStateQuery()

  if (appStateQuery.isLoading) {
    return <LoadingWidget />
  }

  // Some routes have dedicated loading skeletons, so prefer to show them
  // instead of the default loading widget
  function renderLoadingFallback() {
    const routesWithOwnFallback = [ROUTES.referrals]

    return matchesPaths(location.pathname, routesWithOwnFallback) ? null : (
      <LoadingWidget />
    )
  }

  return (
    <>
      <div className="wrapper">
        <Sidebar items={[{ pages: sidebarLinks }]} />

        <Main>
          <Navbar />

          <PageContent padded={padded}>
            <SentryErrorBoundary key={location.pathname}>
              <Suspense fallback={renderLoadingFallback()}>{children}</Suspense>
            </SentryErrorBoundary>
          </PageContent>

          <Footer />
        </Main>
      </div>
    </>
  )
}

export default GroupDashboardLayout
